"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import BrandLogo from "@/components/shared/BrandLogo";

const navLinks = [
  { label: "Home", href: "/#home" },
  { label: "Plans", href: "/#plans" },
  { label: "Referral", href: "/#referral" },
  { label: "Investment sectors", href: "/investment-sectors" },
  { label: "FAQ", href: "/#faq" },
  { label: "About", href: "/#about" },
  { label: "Contact", href: "/#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("panel-theme") === "light" ? "light" : "dark";
    setTheme(saved);
    document.documentElement.classList.toggle("light-theme", saved === "light");
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      document.documentElement.classList.toggle("light-theme", next === "light");
      localStorage.setItem("panel-theme", next);
      return next;
    });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-100 border-b transition-all duration-300 ${
        scrolled
          ? "left-0 right-0 w-full border-border bg-bg-card/95 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl 2xl:px-6 xl:px-5 lg:px-4 md:px-4  px-4">
        <div className="flex h-[72px] items-center justify-between">
          <BrandLogo href="/" size="nav" priority variant="auto" />

          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors ${
                  theme === "light"
                    ? "text-accent-orange hover:text-accent-red"
                    : "text-text-muted hover:text-accent-amber"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-bg-secondary px-5 py-2.5 text-sm font-medium text-text-primary transition hover:border-accent-amber/40"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? <Sun size={16} className="shrink-0 text-accent-amber" /> : <Moon size={16} className="shrink-0 text-text-muted" />}
              <span className="hidden sm:inline">{theme === "dark" ? "Light" : "Dark"}</span>
            </button>
            <Link
              href="/user"
              className="hidden rounded-lg bg-linear-to-br from-accent-amber to-accent-orange px-5 py-2.5 font-display text-sm font-semibold text-white shadow-[0_8px_24px_rgba(245,158,11,0.25)] transition hover:-translate-y-0.5 hover:opacity-90 md:inline-flex"
            >
              Get Started
            </Link>
            <button
              className="cursor-pointer bg-transparent p-1 text-text-primary md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-border bg-bg-card/95 md:hidden"
          >
            <div className="flex flex-col gap-4 px-6 pb-6 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`text-base font-medium no-underline transition-colors ${
                    theme === "light"
                      ? "text-accent-orange hover:text-accent-red"
                      : "text-text-primary hover:text-accent-amber"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                href="/user"
                onClick={() => setMobileOpen(false)}
                className="rounded-lg bg-linear-to-br from-accent-amber to-accent-orange px-5 py-3 text-center font-display text-sm font-semibold text-white no-underline"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
